import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { foldersApi } from '../../api/endpoints/foldersApi';
import {
  Folder,
  FolderTreeNode,
  CreateFolderRequest,
  UpdateFolderRequest,
  MoveFolderRequest,
} from '../../api/types/FolderTypes';

interface FolderState {
  folders: Folder[];
  folderTree: FolderTreeNode[];
  currentFolder: Folder | null;
  loading: boolean;
  error: string | null;
  expandedFolderIds: string[]; // Track which folders are expanded in the tree
}

const initialState: FolderState = {
  folders: [],
  folderTree: [],
  currentFolder: null,
  loading: false,
  error: null,
  expandedFolderIds: [],
};

// Async Thunks

export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (workspaceId: string) => {
    const response = await foldersApi.getFolders(workspaceId);
    return response;
  }
);

export const fetchFolder = createAsyncThunk(
  'folders/fetchFolder',
  async (folderId: string) => {
    const response = await foldersApi.getFolder(folderId);
    return response.folder;
  }
);

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async ({ workspaceId, data }: { workspaceId: string; data: CreateFolderRequest }) => {
    const response = await foldersApi.createFolder(workspaceId, data);
    return response.folder;
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async ({ folderId, data }: { folderId: string; data: UpdateFolderRequest }) => {
    const response = await foldersApi.updateFolder(folderId, data);
    return response.folder;
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (folderId: string) => {
    await foldersApi.deleteFolder(folderId);
    return folderId;
  }
);

export const moveFolder = createAsyncThunk(
  'folders/moveFolder',
  async ({ folderId, data }: { folderId: string; data: MoveFolderRequest }) => {
    const response = await foldersApi.moveFolder(folderId, data);
    return response.folder;
  }
);

export const reorderFolders = createAsyncThunk(
  'folders/reorderFolders',
  async (folderIds: string[]) => {
    await foldersApi.reorderFolders({ folderIds });
    return folderIds;
  }
);

// Slice

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    toggleFolderExpanded: (state, action: PayloadAction<string>) => {
      const folderId = action.payload;
      if (state.expandedFolderIds.includes(folderId)) {
        state.expandedFolderIds = state.expandedFolderIds.filter((id) => id !== folderId);
      } else {
        state.expandedFolderIds.push(folderId);
      }
    },
    expandFolder: (state, action: PayloadAction<string>) => {
      const folderId = action.payload;
      if (!state.expandedFolderIds.includes(folderId)) {
        state.expandedFolderIds.push(folderId);
      }
    },
    collapseFolder: (state, action: PayloadAction<string>) => {
      const folderId = action.payload;
      state.expandedFolderIds = state.expandedFolderIds.filter((id) => id !== folderId);
    },
    expandAllFolders: (state) => {
      state.expandedFolderIds = state.folders.map((folder) => folder.id);
    },
    collapseAllFolders: (state) => {
      state.expandedFolderIds = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Folders
    builder.addCase(fetchFolders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFolders.fulfilled, (state, action) => {
      state.loading = false;
      state.folders = action.payload.folders;
      state.folderTree = action.payload.tree;
    });
    builder.addCase(fetchFolders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch folders';
    });

    // Fetch Single Folder
    builder.addCase(fetchFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFolder.fulfilled, (state, action) => {
      state.loading = false;
      state.currentFolder = action.payload;
    });
    builder.addCase(fetchFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch folder';
    });

    // Create Folder
    builder.addCase(createFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createFolder.fulfilled, (state, action) => {
      state.loading = false;
      state.folders.push(action.payload);
      // Folder tree will be refreshed by fetchFolders
    });
    builder.addCase(createFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to create folder';
    });

    // Update Folder
    builder.addCase(updateFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateFolder.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.folders.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.folders[index] = action.payload;
      }
      if (state.currentFolder?.id === action.payload.id) {
        state.currentFolder = action.payload;
      }
    });
    builder.addCase(updateFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to update folder';
    });

    // Delete Folder
    builder.addCase(deleteFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteFolder.fulfilled, (state, action) => {
      state.loading = false;
      state.folders = state.folders.filter((f) => f.id !== action.payload);
      if (state.currentFolder?.id === action.payload) {
        state.currentFolder = null;
      }
      // Folder tree will be refreshed by fetchFolders
    });
    builder.addCase(deleteFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to delete folder';
    });

    // Move Folder
    builder.addCase(moveFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(moveFolder.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.folders.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.folders[index] = action.payload;
      }
      // Folder tree will be refreshed by fetchFolders
    });
    builder.addCase(moveFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to move folder';
    });

    // Reorder Folders
    builder.addCase(reorderFolders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(reorderFolders.fulfilled, (state) => {
      state.loading = false;
      // Folder tree will be refreshed by fetchFolders
    });
    builder.addCase(reorderFolders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to reorder folders';
    });
  },
});

export const {
  toggleFolderExpanded,
  expandFolder,
  collapseFolder,
  expandAllFolders,
  collapseAllFolders,
  clearError,
} = folderSlice.actions;

export default folderSlice.reducer;
